import { Autocomplete, AutocompleteProps, TextFieldProps } from "@mui/material";
import React, { useState } from "react";
import { IGWidget, appendClass } from "../GWidget";
import { ISelectRow, TSelectValue } from "../dropdown/Dropdown"
import { IFormFieldProps, useValidator } from "../form/Validators";
import GTextField from "./GTextField";
import _ from "lodash";

export interface IGAutoCompleteProps<T extends TSelectValue> extends IGWidget, Omit<IFormFieldProps<T>,"value"> {
    value?:ISelectRow<T>
    options:ISelectRow<T>[]
    textProps?:Omit<TextFieldProps, "error"|"value"|"form">;
    canClear?:boolean;
}

export interface IAutocompleteProps<T extends TSelectValue, FREE_SOLO extends boolean> extends 
    Omit<AutocompleteProps<ISelectRow<T>,false,false,FREE_SOLO>,"options"|"onChange"|"renderInput"|"value"> {}

export interface IGTextSelectProps<T extends TSelectValue> extends IAutocompleteProps<T,false>, IGAutoCompleteProps<T> {
    onChange?: (e:any, value:ISelectRow<T>|null) => void
}

export function isSelect<T extends TSelectValue>(selected:ISelectRow<T>|TSelectValue|null|undefined) : selected is ISelectRow<T> {
    return selected && selected instanceof Object ? true : false;
}    
export function getValue<T extends TSelectValue>(value:ISelectRow<T>|TSelectValue|null|undefined) : TSelectValue|null {
    return isSelect(value) ? value.value : value || null
}
export function getLabel<T extends TSelectValue>(selected:ISelectRow<T>|null|undefined) : string {
    return typeof(selected)=="undefined" || selected===null ? "" : (isSelect(selected) ? selected.label : ""+selected)
}

export default function GTextSelect<T extends TSelectValue>(props:IGTextSelectProps<T>){
    const {onChange,floatLabel,textProps,id,className,canClear,value,form,controlName,...acProps} = props
    const [textValue,setTextValue] = useState<string|null>(getLabel(value) || "")
    const {control,hasError,hint} = useValidator({...props,value:props.value?.value})
    const _onSelect = (e:React.SyntheticEvent,value:ISelectRow<T>|null) => {
        form?.setDirty(true)
        control?.setDirty(true)
        control?.update(value?.value)
        onChange?.(e, value)
        if (value) setTextValue(getLabel(value))
        else setTextValue(null)
    }
    const _onChange = (e:React.SyntheticEvent,value:string) => setTextValue(value)
    const _isEqual = (option:ISelectRow<T>, value:ISelectRow<T>) => option.value==value.value
    const inputProps : Record<string,any> = {}
    if (hasError) inputProps.error = true
    if (hint) inputProps.helperText = hint
    return <Autocomplete className={appendClass(canClear?"":"no-clear", className)} {...acProps}
        onChange={_onSelect} onInputChange={_onChange} getOptionLabel={o => o.label} isOptionEqualToValue={_isEqual} 
        renderInput={(params) => <GTextField {...params} {...textProps} id={id} value={textValue||""} floatLabel={floatLabel} {...inputProps}/>}/>
}
